(() => {
if (customElements.get('snkr-video-carousel')) return;

/*
 * Vu sur TikTok — carrousel de vidéos.
 * - Les cartes visibles se lancent automatiquement en muet (aperçu).
 * - Un clic sur la vidéo révèle le bouton « Activer le son » (en bas à droite) ;
 *   ce bouton bascule le son. Une seule vidéo à la fois joue avec le son.
 * - Les extrémités du carrousel sont fondues (mode « toujours » ou « selon le défilement »).
 */
class SnkrVideoCarousel extends HTMLElement {
  constructor() {
    super();
    this.autoplayMuted = this.dataset.autoplay === 'true';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.visibleCards = new Set();
  }

  connectedCallback() {
    this.listeners?.abort();
    this.listeners = new AbortController();
    this.cards = Array.from(this.querySelectorAll('.snkr-vs__card'));
    this.carousel = this.querySelector('.snkr-vs__carousel');
    this.carouselWrap = this.querySelector('.snkr-vs__carousel-wrap');
    this.prevBtn = this.querySelector('.snkr-vs__nav-btn--prev');
    this.nextBtn = this.querySelector('.snkr-vs__nav-btn--next');
    this.visibleCards.clear();
    this.setupListeners();
    this.setupIntersectionObserver();
    this.updateScrollState();
    if (this.carousel) {
      this.resizeObserver = new ResizeObserver(() => this.updateScrollState());
      this.resizeObserver.observe(this.carousel);
    }
  }

  disconnectedCallback() {
    this.pauseAll();
    this.listeners?.abort();
    this.resizeObserver?.disconnect();
    if (this.observer) this.observer.disconnect();
  }

  setupListeners() {
    const signal = this.listeners.signal;

    this.cards.forEach(card => {
      const video = card.querySelector('video');
      const tapBtn = card.querySelector('.snkr-vs__tap');
      const soundBtn = card.querySelector('.snkr-vs__sound-btn');
      if (!video || !tapBtn) return;

      // Clic sur la vidéo : on révèle le bouton du son et on s'assure que l'aperçu tourne.
      tapBtn.addEventListener('click', () => {
        card.classList.add('is-revealed');
        if (video.paused) {
          this.playVideo(card, video, { silent: false });
        }
      }, { signal });

      if (soundBtn) {
        soundBtn.addEventListener('click', () => {
          card.classList.add('is-revealed');
          if (card.classList.contains('is-unmuted')) {
            this.setMuted(card, video, true);
            return;
          }
          this.muteAll(card);
          this.setMuted(card, video, false);
          if (video.paused) {
            this.playVideo(card, video, { silent: false });
          }
        }, { signal });
      }

      video.addEventListener('error', () => {
        card.classList.add('has-error');
        this.pauseVideo(card, video);
      }, { signal });

      video.addEventListener('pause', () => {
        card.classList.remove('is-playing');
        this.updateTapLabel(card, false);
      }, { signal });
      video.addEventListener('playing', () => {
        card.classList.add('is-playing');
        card.classList.remove('has-error');
        this.updateTapLabel(card, true);
      }, { signal });
    });

    if (this.prevBtn && this.nextBtn && this.carousel) {
      this.prevBtn.addEventListener('click', () => this.scroll(-1), { signal });
      this.nextBtn.addEventListener('click', () => this.scroll(1), { signal });
    }

    if (this.carousel) {
      this.carousel.addEventListener('scroll', () => {
        if (this.scrollRaf) return;
        this.scrollRaf = requestAnimationFrame(() => {
          this.scrollRaf = null;
          this.updateScrollState();
        });
      }, { passive: true, signal });
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Onglet masqué : son coupé partout, puis pause ; au retour, tout redémarre en muet.
        this.muteAll(null);
        this.pauseAll();
      } else {
        this.visibleCards.forEach(card => this.tryAutoplay(card));
      }
    }, { signal });

    // Une fenêtre d'achat rapide s'ouvre : on coupe le son, l'aperçu muet continue.
    document.addEventListener('quick-view:open', () => this.muteAll(null), { signal });

    document.addEventListener('shopify:block:select', (e) => {
      const blockId = e.detail && e.detail.blockId;
      if (!blockId || !this.carousel) return;
      const card = this.querySelector('.snkr-vs__card[data-block-id="' + blockId + '"]');
      if (!card) return;
      const scrollLeft = card.offsetLeft - this.carousel.offsetLeft;
      this.carousel.scrollTo({ left: scrollLeft, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }, { signal });
  }

  setupIntersectionObserver() {
    if (this.observer) this.observer.disconnect();
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const card = entry.target;
        const video = card.querySelector('video');
        if (!video) return;

        if (entry.isIntersecting) {
          this.visibleCards.add(card);
          this.tryAutoplay(card);
        } else {
          // Carte hors champ : pause, retour au muet, bouton du son masqué à nouveau.
          this.visibleCards.delete(card);
          this.pauseVideo(card, video);
          this.setMuted(card, video, true);
          card.classList.remove('is-revealed');
        }
      });
    }, { threshold: 0.5 });

    this.cards.forEach(card => this.observer.observe(card));
  }

  tryAutoplay(card) {
    const video = card.querySelector('video');
    if (!video || !this.autoplayMuted || this.reducedMotion || document.hidden) return;
    if (!video.paused || card.classList.contains('has-error')) return;
    // Tout démarrage automatique se fait en muet, sans exception.
    this.setMuted(card, video, true);
    this.playVideo(card, video, { silent: true });
  }

  playVideo(card, video, { silent }) {
    if (video.readyState === 0) {
      video.load();
    }

    card.classList.remove('has-error');

    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.then(() => {
        card.classList.add('is-playing');
        this.updateTapLabel(card, true);
      }).catch(err => {
        if (err.name === 'AbortError') return;
        card.classList.remove('is-playing');
        if (!silent) {
          console.warn('Video play failed:', err);
          card.classList.add('has-error');
        }
        // Lecture auto refusée par le navigateur : la carte garde son bouton lecture,
        // un clic du visiteur relancera la vidéo.
      });
    }
  }

  pauseVideo(card, video) {
    if (!video.paused) {
      video.pause();
    }
    card.classList.remove('is-playing');
    this.updateTapLabel(card, false);
  }

  // Le bouton transparent sur la vidéo décrit l'action réelle : lecture si la vidéo est
  // arrêtée, affichage du bouton du son si l'aperçu tourne déjà.
  updateTapLabel(card, playing) {
    const tapBtn = card.querySelector('.snkr-vs__tap');
    if (!tapBtn) return;
    const label = playing ? tapBtn.dataset.labelSound : tapBtn.dataset.labelPlay;
    if (label) tapBtn.setAttribute('aria-label', label);
  }

  pauseAll() {
    (this.cards || []).forEach(card => {
      const video = card.querySelector('video');
      if (video) this.pauseVideo(card, video);
    });
  }

  setMuted(card, video, muted) {
    video.muted = muted;
    card.classList.toggle('is-unmuted', !muted);
    const soundBtn = card.querySelector('.snkr-vs__sound-btn');
    if (!soundBtn) return;
    const label = muted ? soundBtn.dataset.labelOff : soundBtn.dataset.labelOn;
    soundBtn.setAttribute('aria-pressed', muted ? 'false' : 'true');
    if (label) {
      soundBtn.setAttribute('aria-label', label);
      const text = soundBtn.querySelector('.snkr-vs__sound-text');
      if (text) text.textContent = label;
    }
  }

  // Coupe le son de toutes les vidéos de la page (toutes sections confondues), sauf `except`.
  muteAll(except) {
    document.querySelectorAll('.snkr-vs__card.is-unmuted').forEach(otherCard => {
      if (otherCard === except) return;
      const otherVideo = otherCard.querySelector('video');
      if (otherVideo) this.setMuted(otherCard, otherVideo, true);
    });
  }

  scroll(direction) {
    if (!this.carousel) return;
    const cardWidth = this.cards[0]?.offsetWidth || 300;
    const gap = parseInt(getComputedStyle(this.carousel).gap) || 0;
    const scrollAmount = (cardWidth + gap) * direction;
    this.carousel.scrollBy({ left: scrollAmount, behavior: this.reducedMotion ? 'auto' : 'smooth' });
  }

  updateScrollState() {
    if (!this.carousel) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.carousel;
    const atStart = scrollLeft <= 10;
    const atEnd = scrollLeft >= scrollWidth - clientWidth - 10;

    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;

    if (this.carouselWrap) {
      this.carouselWrap.classList.toggle('is-start', atStart);
      this.carouselWrap.classList.toggle('is-end', atEnd);
    }
  }
}

customElements.define('snkr-video-carousel', SnkrVideoCarousel);
})();
